import type { Place } from "./types";
import {
  advanceVoiceNavigation,
  initialVoiceNavigation,
  VOICE_NAVIGATION_TIMEOUT_MS,
  type VoiceNavigationState,
} from "./voiceNavigation";

export interface VoiceNavigationView {
  message: string;
  query: string;
  choices: Place[];
  selectedIndex: number;
}

export interface VoiceNavigationDependencies {
  search: (query: string, signal: AbortSignal) => Promise<Place[]>;
  navigate: (place: Place, signal: AbortSignal) => Promise<void>;
  announce: (message: string, lifecycle: VoiceAnnouncement) => (() => void) | void;
  changed: (view: VoiceNavigationView) => void;
  now?: () => number;
}

export interface VoiceAnnouncement {
  isCurrent: () => boolean;
  /** True only after actual successful playback, never after preparation. */
  onComplete: (played: boolean) => void;
}

const emptyView = (): VoiceNavigationView => ({
  message: "",
  query: "",
  choices: [],
  selectedIndex: 0,
});

function validPlace(place: Place): boolean {
  return (
    typeof place?.name === "string" &&
    !!place.name.trim() &&
    typeof place.address === "string" &&
    Number.isFinite(place.lat) &&
    Number.isFinite(place.lng) &&
    Math.abs(place.lat) <= 90 &&
    Math.abs(place.lng) <= 180
  );
}

/**
 * Owns address search and candidate confirmation while the foreground voice
 * listener owns the microphone. All effects are injected for testing. Updating
 * dependencies each render avoids stale UI/location/settings closures.
 */
export class VoiceNavigationSession {
  public dependencies: VoiceNavigationDependencies | undefined;
  private conversation: VoiceNavigationState = initialVoiceNavigation();
  private currentView = emptyView();
  private revision = 0;
  private operation: AbortController | undefined;
  private searching = false;
  private navigating = false;
  private announcementRevision = 0;
  private cancelAnnouncement: (() => void) | undefined;
  private announcedCandidate: string | undefined;

  constructor(dependencies?: VoiceNavigationDependencies) {
    this.dependencies = dependencies;
  }

  get view(): VoiceNavigationView {
    return {
      ...this.currentView,
      choices: this.currentView.choices.map((place) => ({ ...place })),
    };
  }

  private get configured(): VoiceNavigationDependencies {
    if (!this.dependencies)
      throw new Error("Voice navigation is not configured");
    return this.dependencies;
  }

  private now(): number {
    return this.configured.now?.() ?? Date.now();
  }

  private update(view: VoiceNavigationView, announce = true): void {
    this.currentView = view;
    this.dependencies?.changed(this.view);
    if (announce && view.message) this.announce(view.message);
  }

  private message(message: string, announce = true): void {
    this.update({ ...this.currentView, message }, announce);
  }

  private candidateKey(): string {
    const { query, selectedIndex, choices } = this.currentView;
    return JSON.stringify([query, selectedIndex, choices[selectedIndex]]);
  }

  private stopAnnouncement(): void {
    this.announcementRevision++;
    const cancel = this.cancelAnnouncement;
    this.cancelAnnouncement = undefined;
    cancel?.();
  }

  private announce(message: string, candidate?: string): void {
    this.stopAnnouncement();
    const revision = this.revision;
    const announcement = this.announcementRevision;
    const isCurrent = () =>
      revision === this.revision && announcement === this.announcementRevision;
    const cancel = this.dependencies?.announce(message, {
      isCurrent,
      onComplete: (played) => {
        if (!isCurrent() || candidate === undefined || candidate !== this.candidateKey()) return;
        // Give the user a fresh explicit-confirm/retry window after a bounded
        // cold provider request finally succeeds or fails.
        if (this.conversation.phase === "ready")
          this.conversation = { ...this.conversation, updatedAt: this.now() };
        if (played && this.conversation.phase === "ready") {
          this.announcedCandidate = candidate;
        } else {
          this.announcedCandidate = undefined;
          this.message("היעד לא הוקרא. אמרו אנטר לניסיון נוסף, כתובת אחרת או ביטול.", false);
        }
      },
    });
    if (isCurrent()) this.cancelAnnouncement = cancel || undefined;
    else cancel?.();
  }

  private invalidate(): void {
    this.stopAnnouncement();
    this.revision++;
    this.operation?.abort();
    this.operation = undefined;
    this.searching = false;
    this.navigating = false;
  }

  /** Stop/reset on microphone disable, page hiding, or a manually chosen route. */
  reset(): void {
    this.invalidate();
    this.announcedCandidate = undefined;
    this.conversation = initialVoiceNavigation();
    this.update(emptyView(), false);
  }

  private beginOperation(signal: AbortSignal) {
    this.invalidate();
    const revision = this.revision;
    const controller = new AbortController();
    this.operation = controller;
    const abort = () => controller.abort();
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) controller.abort();
    return {
      revision,
      signal: controller.signal,
      finish: () => {
        signal.removeEventListener("abort", abort);
        if (this.operation === controller) this.operation = undefined;
      },
    };
  }

  private announceCandidate(): void {
    const { choices, selectedIndex } = this.currentView;
    const place = choices[selectedIndex];
    if (!place) return;
    const location = (
      place.address.trim() && place.address.trim() !== place.name.trim()
        ? `${place.name}, ${place.address}`
        : place.name
    ).slice(0, 95);
    this.announcedCandidate = undefined;
    const message =
      choices.length > 1
        ? `מצאתי את ${location}. תוצאה ${selectedIndex + 1} מתוך ${choices.length}. אמרו אנטר לניווט או הבא לתוצאה אחרת.`
        : `מצאתי את ${location}. אמרו אנטר כדי לנווט, או כתובת חדשה.`;
    this.message(message, false);
    this.announce(message, this.candidateKey());
  }

  /** Returns false only when the utterance may be parsed as a road report. */
  async handle(text: string, signal: AbortSignal): Promise<boolean> {
    signal.throwIfAborted();
    const previous = this.conversation;
    const transition = advanceVoiceNavigation(previous, text, this.now());
    if (
      this.navigating &&
      previous.phase === "idle" &&
      transition.state.phase === "idle" &&
      transition.action.kind === "prompt"
    ) {
      this.message("הניווט מתחיל. ממתינים למיקום ולמסלול.");
      return true;
    }
    this.conversation = transition.state;
    const action = transition.action;
    if (action.kind === "passthrough") return false;

    if (action.kind === "cancel") {
      this.reset();
      this.message("החיפוש הקולי בוטל.");
      return true;
    }

    if (action.kind === "prompt") {
      // A restarted/invalid/expired address invalidates any pending result. A
      // reminder while already ready must preserve the selected destination.
      if (transition.state.phase !== "ready") {
        this.invalidate();
        this.update({ ...emptyView(), message: action.message });
      } else {
        this.message(action.message);
      }
      return true;
    }

    if (action.kind === "select") {
      if (
        action.query !== this.currentView.query ||
        !this.currentView.choices.length
      ) {
        this.message(
          this.searching
            ? "החיפוש עדיין מתבצע. המתינו לשם היעד."
            : "לא נמצא יעד לבחירה. אמרו כתובת חדשה.",
        );
        return true;
      }
      const count = this.currentView.choices.length;
      this.currentView.selectedIndex =
        (this.currentView.selectedIndex + action.delta + count) % count;
      this.announceCandidate();
      return true;
    }

    if (action.kind === "search") {
      this.announcedCandidate = undefined;
      const operation = this.beginOperation(signal);
      this.searching = true;
      this.update(
        {
          ...emptyView(),
          query: action.query,
          message: `מחפש את ${action.query}.`,
        },
        false,
      );
      try {
        const found = await this.configured.search(
          action.query,
          operation.signal,
        );
        if (operation.revision !== this.revision) return true;
        if (operation.signal.aborted) {
          this.reset();
          return true;
        }
        // Network latency must not silently extend a conversation's validity.
        const now = this.now();
        if (
          this.conversation.phase !== "ready" ||
          this.conversation.query !== action.query ||
          now < this.conversation.updatedAt ||
          now - this.conversation.updatedAt >= VOICE_NAVIGATION_TIMEOUT_MS
        ) {
          this.reset();
          this.message("החיפוש הקולי הסתיים. אמרו ניווט כדי להתחיל חיפוש חדש.");
          return true;
        }
        this.searching = false;
        const choices = found
          .filter(validPlace)
          .slice(0, 8)
          .map((place) => ({ ...place }));
        this.update(
          { message: "", query: action.query, choices, selectedIndex: 0 },
          false,
        );
        // Even an inline enter is not confirmation of an unannounced candidate.
        if (choices.length) this.announceCandidate();
        else
          this.message(
            "לא מצאתי את הכתובת. אמרו כתובת מלאה עם שם היישוב, או ביטול.",
          );
      } catch (error) {
        if (operation.revision !== this.revision) return true;
        this.searching = false;
        if (operation.signal.aborted) this.reset();
        else
          this.message(
            "חיפוש הכתובת לא הצליח. אמרו שוב את הכתובת כדי לנסות מחדש.",
          );
      } finally {
        operation.finish();
      }
      return true;
    }

    // The query itself and the successful search must both still match: enter
    // during a replacement search cannot accidentally use the previous result.
    const place =
      action.query === this.currentView.query
        ? this.currentView.choices[this.currentView.selectedIndex]
        : undefined;
    if (!place) {
      this.conversation =
        previous.phase === "ready"
          ? { ...previous, updatedAt: this.now() }
          : initialVoiceNavigation();
      this.message(
        this.searching
          ? "החיפוש עדיין מתבצע. המתינו לשם היעד ואז אמרו אנטר."
          : "אין יעד מאושר לניווט. אמרו כתובת חדשה.",
      );
      return true;
    }

    if (this.announcedCandidate !== this.candidateKey()) {
      // Enter during preparation or after a playback failure retries the
      // announcement. A second enter after successful playback confirms it.
      this.conversation = { phase: "ready", query: action.query, updatedAt: this.now() };
      this.announceCandidate();
      return true;
    }

    const operation = this.beginOperation(signal);
    this.navigating = true;
    this.message(`מתחיל ניווט אל ${place.name}.`, false);
    try {
      await this.configured.navigate(place, operation.signal);
      if (operation.revision !== this.revision) return true;
      this.navigating = false;
      if (operation.signal.aborted) this.reset();
      else
        this.update(
          { ...emptyView(), message: `הניווט אל ${place.name} התחיל.` },
          false,
        );
    } catch (error) {
      if (operation.revision !== this.revision) return true;
      this.navigating = false;
      if (operation.signal.aborted) this.reset();
      else {
        this.conversation = {
          phase: "ready",
          query: action.query,
          updatedAt: this.now(),
        };
        this.message(
          error instanceof Error && error.message
            ? error.message
            : "לא ניתן להתחיל ניווט. בדקו הרשאת מיקום ואמרו אנטר כדי לנסות שוב.",
        );
      }
    } finally {
      operation.finish();
    }
    return true;
  }
}
